import React, { Component, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Card, Button } from '../ui';
import './ProjectErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ProjectErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Project component error:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Card className="project-error-boundary">
          <div className="project-error-boundary__icon">
            <AlertCircle size={48} />
          </div>
          <h3 className="project-error-boundary__title">Something went wrong</h3>
          <p className="project-error-boundary__message">
            {this.state.error?.message || 'An unexpected error occurred while loading this component.'}
          </p>
          <Button variant="primary" onClick={this.handleRetry}>
            <RefreshCw size={16} />
            Try Again
          </Button>
        </Card>
      );
    }

    return this.props.children;
  }
}

// Functional error display component for API errors
interface ProjectErrorProps {
  error: string;
  onRetry?: () => void;
  onDismiss?: () => void;
}

export const ProjectError: React.FC<ProjectErrorProps> = ({
  error,
  onRetry,
  onDismiss,
}) => {
  return (
    <div className="project-error">
      <div className="project-error__content">
        <AlertCircle size={16} />
        <span>{error}</span>
      </div>
      <div className="project-error__actions">
        {onRetry && (
          <Button variant="ghost" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
        {onDismiss && (
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProjectErrorBoundary;
